import {inject, Injectable} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {
  ContextBarComponentOption,
  ContextBarOptionGroup,
  ContextBarOptionTypes
} from '@q9elements/ui-kit/components';
import {BaseContextBar} from '../base-context-bar';
import {ITextJson} from 'src/app/diagram-data/diagram-json-handler.service';
import {FontSizePickerComponent} from './components/font-size-picker.component';
import {menuItems} from '../../context-menu/menu-items';
import {FONTS} from 'src/app/shared/constants/fonts.constants';
import {ColorPickerComponent} from './components/color-picker.component';
import {STYLES} from 'src/app/editor-interface/shared/models/styles.model';
import {SelectionTypes} from 'src/app/shared/models/diagram-interactions.interface';
import {TextCommands} from 'src/app/commands/text/text.commands';
import {UndoService} from 'src/app/commands/undo.service';

interface ColorPickerOptionParams {
  param: STYLES;
  tooltip: string;
  isRounded?: boolean;
  q9Track?: string;
}

interface FormattingOptionParams {
  param: STYLES;
  icon: string;
  tooltip?: string;
  q9Track?: string;
}

@Injectable()
export class TextboxContextBar extends BaseContextBar {
  public selectionType = SelectionTypes.Text;

  private textJson: ITextJson;
  private translateService = inject(TranslateService);
  private textCommands = inject(TextCommands);
  private undoService = inject(UndoService);

  public getOptions(): ContextBarOptionGroup[] | null {
    this.textJson = this.selectedCell.getJson();

    if (!this.textJson.customStyle) {
      return null;
    }

    switch (true) {
      case this.isSelected.one: {
        return [
          [
            this.getColorPickerOption({
              param: STYLES.BACKGROUND_COLOR,
              tooltip: 'CONTEXT_BAR.FILL_COLOR',
              isRounded: false,
              q9Track: 'CONTEXT_TOOLBAR.TEXTBOX.FILL_COLOR'
            })
          ],
          [
            {
              tooltip: 'CONTEXT_BAR.FONT',
              icon: 'text_icon_v2',
              subOptions: FONTS.map(font => {
                return {
                  name: font,
                  action: () => this.changeFormatting(STYLES.FONT, font)
                };
              })
            },
            {
              type: ContextBarOptionTypes.COMPONENT,
              component: FontSizePickerComponent,
              inputs: {
                textJson: this.textJson,
                changeFontSize: (value: string) => this.changeFormatting(STYLES.FONT_SIZE, value)
              },
              tooltip: 'CONTEXT_BAR.FONT_SIZE'
            },
            this.getColorPickerOption({
              param: STYLES.FONT_COLOR,
              tooltip: 'CONTEXT_BAR.FONT_COLOR',
              isRounded: true,
              q9Track: 'CONTEXT_TOOLBAR.TEXTBOX.FONT_COLOR'
            })
          ],
          [
            this.getFormattingOption({
              param: STYLES.FONT_BOLD,
              icon: 'action_icons:edit_bold',
              tooltip: 'CONTEXT_BAR.BOLD',
              q9Track: 'CONTEXT_TOOLBAR.TEXTBOX.BOLD'
            }),
            this.getFormattingOption({
              param: STYLES.FONT_ITALIC,
              icon: 'action_icons:edit_italic',
              tooltip: 'CONTEXT_BAR.ITALIC',
              q9Track: 'CONTEXT_TOOLBAR.TEXTBOX.ITALIC'
            }),
            this.getFormattingOption({
              param: STYLES.FONT_UNDERLINE,
              icon: 'action_icons:edit_underline',
              tooltip: 'CONTEXT_BAR.UNDERLINE',
              q9Track: 'CONTEXT_TOOLBAR.TEXTBOX.UNDERLINE'
            }),
            {
              icon: 'action_icons:edit_align_center',
              tooltip: 'CONTEXT_BAR.HORIZONTAL',
              subOptions: this.getAlignOptions(STYLES.TEXT_ALIGN)
            },
            {
              icon: 'action_icons:vertical_align_center',
              tooltip: 'CONTEXT_BAR.VERTICAL',
              subOptions: this.getAlignOptions(STYLES.VERTICAL_ALIGN)
            }
          ],
          [this.createMenuItemOption(menuItems.copy), this.createMenuItemOption(menuItems.delete)]
        ];
      }

      default:
        return null;
    }
  }

  private getColorPickerOption({
    param,
    tooltip,
    isRounded,
    q9Track
  }: ColorPickerOptionParams): ContextBarComponentOption {
    return {
      type: ContextBarOptionTypes.COMPONENT,
      component: ColorPickerComponent,
      inputs: {
        shapeJson: this.textJson,
        changeStyle: (value, param) => this.changeFormatting(param, value),
        param,
        isRounded
      },
      tooltip,
      q9Track
    };
  }

  private getAlignOptions(align: STYLES.TEXT_ALIGN | STYLES.VERTICAL_ALIGN) {
    const alignOptionsMap = {
      [STYLES.TEXT_ALIGN]: [
        {
          name: 'CONTEXT_BAR.LEFT_ALIGN',
          icon: 'action_icons:edit_align_left',
          q9Track: 'CONTEXT_TOOLBAR.TEXTBOX.LEFT_ALIGN'
        },
        {
          name: 'CONTEXT_BAR.CENTER_ALIGN',
          icon: 'action_icons:edit_align_center',
          q9Track: 'CONTEXT_TOOLBAR.TEXTBOX.CENTER_ALIGN'
        },
        {
          name: 'CONTEXT_BAR.RIGHT_ALIGN',
          icon: 'action_icons:edit_align_right',
          q9Track: 'CONTEXT_TOOLBAR.TEXTBOX.RIGHT_ALIGN'
        }
      ],
      [STYLES.VERTICAL_ALIGN]: [
        {
          name: 'CONTEXT_BAR.TOP_ALIGN',
          icon: 'action_icons:vertical_align_top',
          q9Track: 'CONTEXT_TOOLBAR.TEXTBOX.TOP_ALIGN'
        },
        {
          name: 'CONTEXT_BAR.MIDDLE_ALIGN',
          icon: 'action_icons:vertical_align_center',
          q9Track: 'CONTEXT_TOOLBAR.TEXTBOX.MIDDLE_ALIGN'
        },
        {
          name: 'CONTEXT_BAR.BOTTOM_ALIGN',
          icon: 'action_icons:vertical_align_bottom',
          q9Track: 'CONTEXT_TOOLBAR.TEXTBOX.BOTTOM_ALIGN'
        }
      ]
    };

    return alignOptionsMap[align].map(option => {
      const value = this.translateService.instant(option.name).toLocaleLowerCase();

      return {
        ...option,
        action: () => this.changeFormatting(align, value)
      };
    });
  }

  private getFormattingOption({param, icon, tooltip, q9Track}: FormattingOptionParams) {
    return {
      icon,
      tooltip,
      q9Track,
      action: () => this.changeFormatting(param, !this.textJson.style[param])
    };
  }

  private changeFormatting(param: STYLES, value: unknown) {
    return this.changeStyle(this.textJson, {param, value});
  }

  private changeStyle(cellJson: ITextJson, {param, value}) {
    cellJson.style[param] = value;
    this._sendStyles(cellJson);
  }

  private _sendStyles(cellJson: ITextJson) {
    const command = this.textCommands.changeStyle(
      cellJson.mxKey,
      cellJson.style,
      cellJson.customStyle
    );

    this.undoService.executeCommand(command);
  }
}
